import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { urlBase64ToUint8Array } from '../lib/push.js';

// Inline toggles inside the Notifications profile section. Email goes through
// SendGrid; push goes through web-push + the service worker registered at the
// app root. iOS 16.4+ requires PWA install to home screen before push works —
// the toggle still appears but Notification.requestPermission() will refuse
// until the app is installed.
export function NotificationPrefs({ profile, currentUser, onChanged }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [emailEnabled, setEmailEnabled] = useState(profile?.email_notifications !== false);
  const [pushEnabled, setPushEnabled] = useState(profile?.push_notifications === true);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setPushSupported(supported);
    if (supported && 'Notification' in window) setPushPermission(Notification.permission);
  }, []);

  async function toggleEmail(next) {
    setBusy(true); setMsg('');
    try {
      await api('PUT', `/api/profile/${profile.id}`, { email_notifications: next }, headers);
      setEmailEnabled(next);
      setMsg(`Email ${next ? 'enabled' : 'disabled'}.`);
      onChanged && onChanged();
    } catch (e) { setMsg(`Update failed: ${e.message || e}`); }
    finally { setBusy(false); }
  }

  async function togglePush(next) {
    setBusy(true); setMsg('');
    try {
      if (next) {
        if (!pushSupported) throw new Error('Your browser does not support push notifications.');
        const permission = await Notification.requestPermission();
        setPushPermission(permission);
        if (permission !== 'granted') throw new Error('Notification permission denied.');
        const reg = await navigator.serviceWorker.register('/service-worker.js');
        await navigator.serviceWorker.ready;
        const keyResp = await api('GET', '/api/push/public-key', null, headers);
        if (!keyResp?.publicKey) throw new Error('Push not configured on the server.');
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(keyResp.publicKey),
        });
        const json = sub.toJSON();
        await api('POST', '/api/push/subscribe',
          { endpoint: json.endpoint, keys: json.keys }, headers);
        setPushEnabled(true);
        setMsg('Push enabled on this device.');
      } else {
        if (!('serviceWorker' in navigator)) {
          await api('PUT', `/api/profile/${profile.id}`, { push_notifications: false }, headers);
        } else {
          const reg = await navigator.serviceWorker.getRegistration();
          const sub = reg ? await reg.pushManager.getSubscription() : null;
          if (sub) {
            await api('POST', '/api/push/unsubscribe', { endpoint: sub.endpoint }, headers);
            await sub.unsubscribe();
          } else {
            await api('PUT', `/api/profile/${profile.id}`, { push_notifications: false }, headers);
          }
        }
        setPushEnabled(false);
        setMsg('Push disabled on this device.');
      }
      onChanged && onChanged();
    } catch (e) { setMsg(e.message || 'Update failed.'); setPushEnabled(!next); }
    finally { setBusy(false); }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontSize: 13, color: '#212121' }}>Email notifications</div>
          <div style={{ fontSize: 11, color: '#9e9e9e' }}>Schedule changes, time-off updates, daily messages.</div>
        </div>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={emailEnabled} disabled={busy}
            onChange={e => toggleEmail(e.target.checked)} />
          <span style={{ fontSize: 12, color: emailEnabled ? '#16a34a' : '#9e9e9e', fontWeight: 600 }}>
            {emailEnabled ? 'On' : 'Off'}
          </span>
        </label>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontSize: 13, color: '#212121' }}>Push notifications (this device)</div>
          <div style={{ fontSize: 11, color: '#9e9e9e' }}>
            {!pushSupported ? 'Not supported by this browser.' :
             pushPermission === 'denied' ? 'Permission was denied — re-enable in browser settings.' :
             'Real-time alerts even when Monarch is closed. iPhone: install to Home Screen first.'}
          </div>
        </div>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: pushSupported && pushPermission !== 'denied' ? 'pointer' : 'not-allowed' }}>
          <input type="checkbox" checked={pushEnabled} disabled={busy || !pushSupported || pushPermission === 'denied'}
            onChange={e => togglePush(e.target.checked)} />
          <span style={{ fontSize: 12, color: pushEnabled ? '#16a34a' : '#9e9e9e', fontWeight: 600 }}>
            {pushEnabled ? 'On' : 'Off'}
          </span>
        </label>
      </div>
      {/* Reset notifications: full nuclear option. Unsubscribes from APNS at
          the browser level, deletes the server row, and unregisters the
          service worker. Use when something queued at APNS keeps draining
          (CJ 2026-05-22 — temp-alert backlog from old 24h-TTL pushes). After
          a reset, tap the toggle (or the banner) to re-subscribe fresh. */}
      {pushSupported && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingTop: 4, borderTop: '1px dashed #e0e0e0', marginTop: 4 }}>
          <div style={{ fontSize: 11, color: '#9e9e9e' }}>
            Notifications stuck or unwanted? Reset clears the subscription at the OS level and drops any queued pushes.
          </div>
          <button onClick={async () => {
              if (!confirm('Reset push notifications on this device? You\'ll need to re-enable to receive any pushes.')) return;
              setBusy(true); setMsg('');
              try {
                const reg = await navigator.serviceWorker.getRegistration();
                const sub = reg ? await reg.pushManager.getSubscription() : null;
                if (sub) {
                  try { await api('POST', '/api/push/unsubscribe', { endpoint: sub.endpoint }, headers); } catch (_) {}
                  try { await sub.unsubscribe(); } catch (_) {}
                }
                try { await api('PUT', `/api/profile/${profile.id}`, { push_notifications: false }, headers); } catch (_) {}
                if (reg) { try { await reg.unregister(); } catch (_) {} }
                try { localStorage.removeItem('monarchPushBannerDismissedAt'); } catch (_) {}
                setPushEnabled(false);
                setPushPermission('Notification' in window ? Notification.permission : 'default');
                setMsg('Reset complete. Tap the toggle above (or the banner) to re-enable.');
                onChanged && onChanged();
              } catch (e) { setMsg(`Reset failed: ${e.message || e}`); }
              finally { setBusy(false); }
            }} disabled={busy} className="btn-secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
            Reset
          </button>
        </div>
      )}
      {/* Test button gated to admin (owner + admin role only). Managers and
          staff can flip toggles for themselves but can't fire diagnostic
          notifications — keeps the bell on Bellevue's iPad clean. */}
      {window.EmpireRoles.hasCapability(currentUser, 'adjust_pto_balance') && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingTop: 4, borderTop: '1px dashed #e0e0e0', marginTop: 4 }}>
          <div style={{ fontSize: 11, color: '#9e9e9e' }}>Admin tool — send yourself a test notification to confirm what's wired up.</div>
          <button onClick={async () => {
              setBusy(true); setMsg('');
              try {
                const r = await api('POST', '/api/push/test', {}, headers);
                const b = r?.breakdown || {};
                const lines = ['Test sent.'];
                lines.push(`• In-app: delivered (check the bell)`);
                if (b.email?.configured) {
                  if (b.email.enabled && b.email.has_address) lines.push(`• Email: sent to ${profile.email}`);
                  else if (!b.email.has_address) lines.push(`• Email: skipped — no email on your account`);
                  else lines.push(`• Email: skipped — opted out`);
                } else {
                  lines.push(`• Email: not configured server-side yet`);
                }
                if (b.push?.configured) {
                  if (b.push.enabled && b.push.devices > 0) lines.push(`• Push: sent to ${b.push.devices} device${b.push.devices === 1 ? '' : 's'}`);
                  else if (b.push.devices === 0) lines.push(`• Push: 0 devices subscribed — flip the toggle on from your installed PWA (iOS: Add to Home Screen first)`);
                  else lines.push(`• Push: opted out`);
                } else {
                  lines.push(`• Push: not configured server-side yet`);
                }
                setMsg(lines.join('\n'));
              } catch (e) { setMsg(`Test failed: ${e.message || e}`); }
              finally { setBusy(false); }
            }} disabled={busy} className="btn-secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
            Send test
          </button>
        </div>
      )}
      {msg && <div style={{ fontSize: 11, whiteSpace: 'pre-line', color: msg.includes('failed') || msg.includes('denied') || msg.includes('not support') ? '#dc2626' : '#16a34a' }}>{msg}</div>}
    </div>
  );
}
